/**
 * Hook for managing competitors data in the Knowledge Graph widget.
 *
 * This hook fetches and organizes competitor data from multiple sources:
 * - Company competitors with themes (from ontology API)
 * - Screener data (overview + performance metrics)
 * - Entity details for each competitor
 *
 * Data mapping process:
 * - Screener data is positionally mapped to entities (entities[0] → data[0])
 * - Data is combined by entity tag for both table and drawer access
 *
 * The data is organized into:
 * - tableData: Array of competitor rows for the table display
 * - competitorsByTag: Map of competitor data keyed by entity tag for drawer access
 *
 * @param entityTag - The entity tag to fetch competitors for
 * @returns Object containing table data, competitor map, loading states, and metadata
 */

import {
  CompanyCompetitorsWithThemesResponse,
  Entity,
  FilterResults,
  screener,
  ThemeSchemaResponse,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { QUERY_KEY_SCREENER_RESULTS } from '@terminal/views/screener/results/useScreenerTable';
import {
  extractPerfKGData,
  extractScreenerOverviewData,
} from '@terminal/views/theme-overview/tab-content/utils';
import { useMemo } from 'react';

const PAGE_SIZE = 200;

export type CompetitorData = {
  entity: Entity;
  competitorData: CompanyCompetitorsWithThemesResponse[0];
  competingThemes: ThemeSchemaResponse[];
};

export type CompetitorsByTag = Record<string, CompetitorData>;

export type CompetitorsTableData = {
  entity?: Entity;
  hasData: boolean;
  competingThemes: ThemeSchemaResponse[];
  rank: number;
} & Partial<Record<string, unknown>>;

export const useCompetitorsData = ({ entityTag }: { entityTag: string }) => {
  const { params } = useQueryParams();
  const selectedCompetitor = params[queryKeys.competitorTag];

  const isApiScreenerEntitiesV3FlagEnabled = useFlags(state =>
    state.getFeatureFlagState('DATA-1243-api_screener_entities_v3')
  );
  const {
    data: competitorsData,
    isSuccess: isCompetitorsSuccess,
    isError: isCompetitorsError,
    isFetching: isCompetitorsFetching,
  } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const entityTags = competitorsData
    ? competitorsData.map(item => item.company)
    : [];

  const requestFilters = {
    entity_tag: {
      values: [{ tags: entityTags }],
    },
  };

  const {
    data: screenerData,
    isFetching: isScreenerFetching,
    isError: isScreenerError,
  } = useInfiniteQuery({
    queryKey: [
      QUERY_KEY_SCREENER_RESULTS,
      requestFilters,
      isApiScreenerEntitiesV3FlagEnabled,
    ],
    queryFn: async ({ signal, pageParam }) => {
      const postFilterPath = isApiScreenerEntitiesV3FlagEnabled
        ? screener.postFilterv3.path
        : screener.postFilter.path;
      return await wretchRequest<FilterResults>(`${postFilterPath}`, {
        method: 'post',
        body: {
          filters: requestFilters,
          page: {
            number: pageParam,
            size: PAGE_SIZE,
          },
        },
        signal,
      });
    },
    enabled: !!entityTags.length,
    initialPageParam: 1,
    staleTime: getTimeTillEndOfDayMs(),
    getNextPageParam: lastData => lastData.page.next,
  });

  const entities = screenerData?.pages?.[0]?.entities;
  const latestData = screenerData?.pages?.[0];

  const { data: entitiesData, isLoading: isEntitiesLoading } = useEntities({
    entityTags: entities ?? [],
    appendFetching: true,
    enabled: !isScreenerFetching && !isScreenerError,
  });

  const { columnsMeta, combinedScreenerData } = useMemo(() => {
    const screenerOverview = extractScreenerOverviewData(latestData?.data);
    const screenerPerf = extractPerfKGData(latestData?.data);

    const combinedScreenerData = [
      ...screenerOverview.data,
      ...screenerPerf.data,
    ];
    const columnsMeta = [...screenerOverview.meta, ...screenerPerf.meta];

    return { columnsMeta, combinedScreenerData };
  }, [latestData]);

  const screenerByTag = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    entities?.forEach((entityTag, index) => {
      map.set(entityTag, combinedScreenerData[index] || {});
    });
    return map;
  }, [entities, combinedScreenerData]);

  const organisedData = useMemo(() => {
    if (!competitorsData || !combinedScreenerData || !entitiesData) {
      return null;
    }

    const competitorsByTag: CompetitorsByTag = {};
    const tableData: CompetitorsTableData[] = [];

    competitorsData.forEach(competitor => {
      const entity = entitiesData.find(e => e.tag === competitor.company);

      if (entity) {
        const normalizedTheme = competitor.competing_themes.map(theme => ({
          ...theme,
          parent_theme: theme.parent,
        }));

        competitorsByTag[competitor.company] = {
          entity,
          competitorData: competitor,
          competingThemes: normalizedTheme,
        };

        tableData.push({
          entity,
          hasData: !isEntitiesLoading && !!entity,
          competingThemes: normalizedTheme,
          rank: competitor.relationship.rank,
          ...(screenerByTag.get(competitor.company) || {}),
        });
      }
    });

    return {
      tableData,
      columnsMeta,
      competitorsByTag,
      selectedCompetitor,
    };
  }, [
    competitorsData,
    entitiesData,
    combinedScreenerData,
    columnsMeta,
    selectedCompetitor,
  ]);

  return {
    tableData: organisedData?.tableData ?? [],
    competitorsByTag: organisedData?.competitorsByTag ?? {},
    selectedCompetitor,
    columnsMeta,
    isError: isCompetitorsError || isScreenerError,
    isEmpty: isCompetitorsSuccess && !competitorsData?.length,
    isFetching:
      isCompetitorsFetching || isScreenerFetching || isEntitiesLoading,
  };
};
